import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Logout as LogoutIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  QuestionAnswer as QuestionIcon,
  PersonAdd as PersonAddIcon,
  Psychology as PsychologyIcon,
  Mic as MicIcon,
  PresentToAll as PresentToAllIcon,
  Business as BusinessIcon,
  TrendingUp as StrategyIcon,
  AssignmentTurnedIn as AssessmentIcon,
  Forum as ForumIcon,
  Handshake as HandshakeIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';
import { TimelineEvent, Candidate, Position, Application } from '../../types';
import airtableService from '../../services/airtable';
import candidateAirtableService from '../../services/candidateAirtable';
import CandidateLogin from './CandidateLogin';
import Footer from '../../components/Footer';

interface LinkedTimelineEvent extends TimelineEvent {
  candidate?: Candidate;
  position?: Position;
  application?: Application;
}

interface TimelineStep {
  id: number;
  title: string;
  description: string;
  type: 'interview' | 'administrative' | 'assessment' | 'final';
  icon: React.ReactNode;
  completed: boolean;
  duration: number;
}

const CandidateProfile: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<LinkedTimelineEvent[]>([]);
  const [questionDialog, setQuestionDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);

  // 20-step interview process timeline based on candidate_steps.csv
  const interviewTimelineSteps: TimelineStep[] = [
    // Initial Setup Phase
    { id: 1, title: 'Candidate Activated', description: 'Candidate profile activated in system', type: 'administrative', icon: <PersonAddIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 2, title: 'Candidate Screened', description: 'Initial screening and evaluation', type: 'administrative', icon: <PsychologyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 3, title: 'Candidate Interviewed', description: 'Initial candidate interview with M4 team', type: 'administrative', icon: <MicIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 4, title: 'Candidate Presented', description: 'Candidate presented to client organization', type: 'administrative', icon: <PresentToAllIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // ESP Interview Phase
    { id: 5, title: 'ESP Interview 1', description: 'First Executive Search Partner interview', type: 'interview', icon: <BusinessIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 6, title: 'ESP Interview 2', description: 'Second Executive Search Partner interview', type: 'interview', icon: <BusinessIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 7, title: 'ESP Interview 3', description: 'Third Executive Search Partner interview', type: 'interview', icon: <BusinessIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Strategic Interview Phase
    { id: 8, title: '1st Strategic Interview', description: 'First strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 9, title: '2nd Strategic Interview', description: 'Second strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 10, title: '3rd Strategic Interview', description: 'Third strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Assessment Phase
    { id: 11, title: 'Assessment 1', description: 'First comprehensive candidate assessment', type: 'assessment', icon: <AssessmentIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 12, title: 'Assessment 2', description: 'Second comprehensive candidate assessment', type: 'assessment', icon: <AssessmentIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Continued Strategic Interview Phase
    { id: 13, title: '4th Strategic Interview', description: 'Fourth strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 14, title: '5th Strategic Interview', description: 'Fifth strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 15, title: '6th Strategic Interview', description: 'Sixth strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 16, title: '7th Strategic Interview', description: 'Seventh strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 17, title: '8th Strategic Interview', description: 'Eighth strategic stakeholder interview', type: 'interview', icon: <StrategyIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },

    // Final Decision Phase
    { id: 18, title: 'Post Interviews Debrief', description: 'Comprehensive team debrief after all interviews', type: 'final', icon: <ForumIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 19, title: 'Prefinal Meeting', description: 'Pre-final decision and evaluation meeting', type: 'final', icon: <HandshakeIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 },
    { id: 20, title: 'Final Meeting', description: 'Final decision and offer meeting', type: 'final', icon: <EmojiEventsIcon sx={{ fontSize: 'inherit' }} />, completed: false, duration: 1 }
  ];

  const fetchData = useCallback(async () => {
    if (!candidate) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch timeline events, positions, and applications
      const [
        timelineResponse,
        positionsResponse,
        applicationsResponse
      ] = await Promise.all([
        airtableService.getTimelineEvents({ limit: 50, page: 1 }),
        airtableService.getPositions(),
        airtableService.getApplications()
      ]);

      if (!timelineResponse.success) {
        setError('Failed to fetch timeline events');
        return;
      }

      const positions = positionsResponse.success ? positionsResponse.data : [];
      const applications = applicationsResponse.success ? applicationsResponse.data : [];

      // Filter timeline events for this candidate only
      const candidateTimelineEvents: LinkedTimelineEvent[] = timelineResponse.data
        .filter(event => {
          const application = applications.find(a => a.id === event.applicationId);
          return application?.candidateId === candidate.id;
        })
        .map(event => {
          const application = applications.find(a => a.id === event.applicationId);
          const position = positions.find(p => p.id === application?.positionId);

          return {
            ...event,
            application,
            candidate,
            position
          } as LinkedTimelineEvent;
        })
        .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());

      setTimelineEvents(candidateTimelineEvents);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch candidate progress');
    } finally {
      setLoading(false);
    }
  }, [candidate]);

  useEffect(() => {
    if (authenticated && candidate) {
      fetchData();
    }
  }, [authenticated, candidate, fetchData]);

  const handleAuthenticated = (authenticatedCandidate: Candidate) => {
    setCandidate(authenticatedCandidate);
    setAuthenticated(true);
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setCandidate(null);
    setTimelineEvents([]);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleQuestionSubmit = async () => {
    if (!newQuestion.trim() || !candidate || timelineEvents.length === 0) {
      return;
    }

    try {
      setSubmittingQuestion(true);

      // Get the most recent application ID for this candidate
      const mostRecentEvent = timelineEvents.find(event => event.application?.candidateId === candidate.id);

      if (!mostRecentEvent?.applicationId) {
        setError('Unable to submit question: No application found');
        return;
      }

      // Create a new timeline event with the candidate question
      const response = await candidateAirtableService.createQuestionTimelineEvent(
        mostRecentEvent.applicationId,
        newQuestion.trim(),
        candidate.id
      );

      if (response.success) {
        // Clear the question and close dialog
        setNewQuestion('');
        setQuestionDialog(false);

        // Refresh the timeline events to show the new question
        await fetchData();

        console.log('Question submitted successfully');
      } else {
        setError(`Failed to submit question: ${response.error}`);
      }
    } catch (err) {
      setError(`Error submitting question: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const parseStepNumber = (currentStep: string | number): number => {
    if (typeof currentStep === 'number') {
      return Math.max(1, Math.min(20, currentStep));
    }

    if (typeof currentStep === 'string') {
      const match = currentStep.match(/Step\s*(\d+)/i);
      if (match && match[1]) {
        const stepNumber = parseInt(match[1], 10);
        return Math.max(1, Math.min(20, stepNumber));
      }
    }

    return 1;
  };

  const getCurrentStepForCandidate = (): number => {
    if (!candidate || timelineEvents.length === 0) return 1;

    const eventsWithSteps = timelineEvents
      .filter(event => event.currentStep != null && event.currentStep !== undefined)
      .map(event => ({
        ...event,
        parsedStepNumber: parseStepNumber(event.currentStep!)
      }))
      .filter(event => event.parsedStepNumber > 0)
      .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());

    if (eventsWithSteps.length > 0) {
      return eventsWithSteps[0].parsedStepNumber;
    }

    return 1;
  };

  const getTimelineStepByNumber = (stepNumber: number): TimelineStep | null => {
    return interviewTimelineSteps.find(step => step.id === stepNumber) || null;
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case 'Application Submitted': return 'primary';
      case 'Status Change': return 'secondary';
      case 'Interview Scheduled': return 'info';
      case 'Interview Completed': return 'success';
      case 'Feedback Submitted': return 'warning';
      case 'Offer Extended': return 'success';
      case 'Offer Accepted/Declined': return 'secondary';
      case 'Notes Added': return 'default';
      default: return 'default';
    }
  };

  const formatDateTime = (date: Date, time?: string): string => {
    const dateStr = date.toLocaleDateString();
    return time ? `${dateStr} at ${time}` : dateStr;
  };

  if (!authenticated) {
    return <CandidateLogin onAuthenticated={handleAuthenticated} />;
  }

  if (loading && timelineEvents.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const currentStep = getCurrentStepForCandidate();
  const progressPercentage = Math.round((currentStep / 20) * 100);

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ flex: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} className="no-print">
        <Box display="flex" alignItems="center" gap={2}>
          {candidate?.candidateImage && candidate.candidateImage.length > 0 && (
            <Avatar
              sx={{ width: 60, height: 60, mr: 2 }}
              src={candidate.candidateImage[0].thumbnails?.small?.url || candidate.candidateImage[0].url}
            />
          )}
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {candidate?.firstName} {candidate?.lastName}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Interview Progress Portal
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1}>
          <IconButton onClick={fetchData} color="primary" title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<QuestionIcon />}
            onClick={() => setQuestionDialog(true)}
            sx={{ mr: 1 }}
            color="info"
          >
            Ask Question
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mr: 1 }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            color="error"
          >
            Logout
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Current Status Card */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
                Current Interview Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Step {currentStep} of 20 ({progressPercentage}% Complete)
              </Typography>
            </Box>
            <Chip
              label={candidate?.status || 'Active'}
              color="primary"
              sx={{ fontSize: '1rem', py: 2, px: 1 }}
            />
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                height: 12,
                background: 'linear-gradient(90deg, rgba(96, 165, 250, 0.2) 0%, rgba(96, 165, 250, 0.1) 100%)',
                borderRadius: 6,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${progressPercentage}%`,
                  background: currentStep >= 20
                    ? 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%)'
                    : 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                  borderRadius: 6,
                  transition: 'width 0.5s ease',
                }}
              />
            </Box>
          </Box>

          {/* Current Step Info */}
          {(() => {
            const stepInfo = getTimelineStepByNumber(currentStep);
            return stepInfo ? (
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: stepInfo.type === 'final'
                      ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                      : stepInfo.type === 'interview'
                      ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                      : stepInfo.type === 'assessment'
                      ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                      : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                  }}
                >
                  <Box sx={{ color: 'white', fontSize: '1.2rem' }}>
                    {stepInfo.icon}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    {stepInfo.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stepInfo.description}
                  </Typography>
                </Box>
                <Chip
                  label={
                    stepInfo.type === 'interview' ? 'Interview' :
                    stepInfo.type === 'administrative' ? 'Administrative' :
                    'Final Decision'
                  }
                  size="small"
                  sx={{
                    backgroundColor: stepInfo.type === 'interview'
                      ? 'rgba(59, 130, 246, 0.2)'
                      : stepInfo.type === 'administrative'
                      ? 'rgba(107, 114, 128, 0.2)'
                      : 'rgba(245, 158, 11, 0.2)',
                    color: stepInfo.type === 'interview'
                      ? '#60a5fa'
                      : stepInfo.type === 'administrative'
                      ? '#9ca3af'
                      : '#fbbf24'
                  }}
                />
              </Box>
            ) : null;
          })()}
        </CardContent>
      </Card>

      {/* 20-Step Interview Process Timeline */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              color: '#ffffff',
              mb: 2,
              textAlign: 'center'
            }}
          >
            20-Step Interview Process Timeline
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: 'center',
              mb: 2
            }}
          >
            Current Progress: Step {currentStep}/20 ({progressPercentage}%)
          </Typography>

          {/* Timeline Steps Grid */}
          <Box sx={{ overflowX: 'auto', pb: 2, pt: 3 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(10, 1fr)',
              gap: 1.5,
              minWidth: '800px',
              '@media (max-width: 1200px)': {
                gridTemplateColumns: 'repeat(5, 1fr)',
                minWidth: '600px'
              },
              '@media (max-width: 600px)': {
                gridTemplateColumns: 'repeat(4, 1fr)',
                minWidth: '400px'
              }
            }}>
              {interviewTimelineSteps.map((step) => {
                const isCompleted = step.id <= currentStep;
                const isCurrent = step.id === currentStep;
                const stepToRender = { ...step, completed: isCompleted };

                return (
                  <Box
                    key={step.id}
                    sx={{
                      position: 'relative',
                      textAlign: 'center',
                      opacity: isCurrent ? 1 : isCompleted ? 0.9 : 0.6,
                      transform: isCurrent ? 'scale(1.05)' : 'scale(1)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Step Circle */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 4px auto',
                        background: (step.id >= 11 && step.id <= 12)
                          ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'  // Light gray for Assessment steps
                          : (step.id >= 5 && step.id <= 7)
                          ? 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)'  // Cyan for ESP interviews
                          : stepToRender.completed
                          ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                          : stepToRender.type === 'final'
                          ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                          : stepToRender.type === 'interview'
                          ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                          : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                        border: `2px solid ${(step.id >= 11 && step.id <= 12)
                          ? '#9ca3af'  // Light gray border for Assessment steps
                          : (step.id >= 5 && step.id <= 7)
                          ? '#06b6d4'  // Cyan border for ESP interviews
                          : stepToRender.completed
                          ? '#10b981'
                          : stepToRender.type === 'final'
                          ? '#f59e0b'
                          : stepToRender.type === 'interview'
                          ? '#3b82f6'
                          : '#6b7280'}`,
                        boxShadow: isCurrent
                          ? `0 0 12px ${(step.id >= 11 && step.id <= 12)
                            ? 'rgba(156, 163, 175, 0.4)'
                            : (step.id >= 5 && step.id <= 7)
                            ? 'rgba(6, 182, 212, 0.4)'
                            : stepToRender.completed
                            ? 'rgba(16, 185, 129, 0.4)'
                            : 'rgba(59, 130, 246, 0.4)'}`
                          : `0 2px 8px ${(step.id >= 11 && step.id <= 12)
                            ? 'rgba(156, 163, 175, 0.2)'
                            : (step.id >= 5 && step.id <= 7)
                            ? 'rgba(6, 182, 212, 0.2)'
                            : stepToRender.completed
                            ? 'rgba(16, 185, 129, 0.2)'
                            : stepToRender.type === 'final'
                            ? 'rgba(245, 158, 11, 0.2)'
                            : stepToRender.type === 'interview'
                            ? 'rgba(59, 130, 246, 0.2)'
                            : 'rgba(107, 114, 128, 0.2)'}`,
                        fontSize: '1rem',
                        color: 'white'
                      }}
                    >
                      {stepToRender.icon}
                    </Box>

                    {/* Step Details */}
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: (step.id >= 11 && step.id <= 12)
                            ? '#9ca3af'  // Light gray text for Assessment steps
                            : (step.id >= 5 && step.id <= 7)
                            ? '#06b6d4'  // Cyan text for ESP interviews
                            : stepToRender.completed
                            ? '#10b981'
                            : '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                          display: 'block',
                          mb: 0.25
                        }}
                      >
                        Step {step.id}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.55rem',
                          display: 'block',
                          lineHeight: 1.1,
                          mb: 0.25
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Chip
                        label={
                          step.id === 11 || step.id === 12 ? 'Prefinal' :
                          step.type === 'interview' ? 'Interview' :
                          step.type === 'administrative' ? 'Admin' :
                          'Final'
                        }
                        size="small"
                        sx={{
                          height: 12,
                          fontSize: '0.45rem',
                          backgroundColor: step.id === 11 || step.id === 12
                            ? 'rgba(16, 185, 129, 0.2)'  // Green background for prefinal steps
                            : step.type === 'interview'
                            ? 'rgba(59, 130, 246, 0.2)'
                            : step.type === 'administrative'
                            ? 'rgba(107, 114, 128, 0.2)'
                            : 'rgba(245, 158, 11, 0.2)',
                          color: step.id === 11 || step.id === 12
                            ? '#10b981'  // Green text for prefinal steps
                            : step.type === 'interview'
                            ? '#60a5fa'
                            : step.type === 'administrative'
                            ? '#9ca3af'
                            : '#fbbf24'
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Progress Legend */}
          <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Administrative (4)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                ESP Interviews (3)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Strategic Interviews (8)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Assessments (2)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Final Decision (3)
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Timeline Events */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: '#ffffff',
          mb: 3,
        }}
      >
        Your Candidacy Progress
      </Typography>

      <Grid container spacing={3}>
        {timelineEvents.map((event, index) => (
          <Grid item xs={12} md={6} key={event.id}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box flex={1}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ color: '#ffffff', fontWeight: 600 }}
                    >
                      {event.position?.title || 'Position Update'}
                    </Typography>
                  </Box>
                  <Chip
                    label={event.eventType}
                    color={getEventTypeColor(event.eventType) as any}
                    size="small"
                  />
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '0.8rem',
                    mb: 2
                  }}
                >
                  {formatDateTime(event.eventDate, event.eventTime)}
                </Typography>

                {event.previousStatus && event.newStatus && (
                  <Box mb={2}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#60a5fa', fontWeight: 500 }}
                    >
                      Status Change:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip label={event.previousStatus} size="small" variant="outlined" />
                      <Typography sx={{ color: '#60a5fa' }}>→</Typography>
                      <Chip label={event.newStatus} size="small" />
                    </Box>
                  </Box>
                )}

                <Typography
                  variant="body2"
                  paragraph
                  sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                >
                  <strong style={{ color: '#60a5fa' }}>Update:</strong> {event.description}
                </Typography>

                {event.notes && (
                  <Typography
                    variant="body2"
                    paragraph
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    <strong style={{ color: '#60a5fa' }}>Notes:</strong> {event.notes}
                  </Typography>
                )}

                {event.nextSteps && (
                  <Typography
                    variant="body2"
                    paragraph
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    <strong style={{ color: '#34d399' }}>Next Steps:</strong> {event.nextSteps}
                  </Typography>
                )}

                {event.availableMeetTimes && (
                  <Typography
                    variant="body2"
                    paragraph
                    sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                  >
                    <strong style={{ color: '#fbbf24' }}>Available Meet Times:</strong> {event.availableMeetTimes}
                  </Typography>
                )}

                {/* Candidate Questions & Admin Answers */}
                {(event.candidateQuestions || event.adminAnswers) && (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(96, 165, 250, 0.1)',
                      border: '1px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: 2,
                      p: 2,
                      my: 2
                    }}
                  >
                    {event.candidateQuestions && (
                      <Box mb={event.adminAnswers ? 2 : 0}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#60a5fa',
                            fontWeight: 600,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          💬 Your Question:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            backgroundColor: 'rgba(96, 165, 250, 0.05)',
                            p: 1.5,
                            borderRadius: 1,
                            fontStyle: 'italic'
                          }}
                        >
                          "{event.candidateQuestions}"
                        </Typography>
                      </Box>
                    )}

                    {event.adminAnswers ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#34d399',
                            fontWeight: 600,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          ✅ M4 Consulting Response:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            backgroundColor: 'rgba(52, 211, 153, 0.05)',
                            p: 1.5,
                            borderRadius: 1
                          }}
                        >
                          {event.adminAnswers}
                        </Typography>
                      </Box>
                    ) : event.candidateQuestions && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontStyle: 'italic',
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        Waiting for response...
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Typical Candidate Questions & Admin Answers */}
                {(event.typicalCandidateQuestions || event.typicalAdminAnswers) && (
                  <Box
                    sx={{
                      backgroundColor: 'rgba(139, 69, 19, 0.1)', // Brown tint to distinguish from dynamic Q&A
                      border: '1px solid rgba(139, 69, 19, 0.3)',
                      borderRadius: 2,
                      p: 2,
                      my: 2
                    }}
                  >
                    {event.typicalCandidateQuestions && (
                      <Box mb={event.typicalAdminAnswers ? 2 : 0}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#d97706',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1
                          }}
                        >
                          <PsychologyIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                          Key Candidate Question:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            lineHeight: 1.6,
                            fontStyle: 'italic'
                          }}
                        >
                          "{event.typicalCandidateQuestions}"
                        </Typography>
                      </Box>
                    )}

                    {event.typicalAdminAnswers ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#059669',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 1
                          }}
                        >
                          <QuestionIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                          Key Candidate Answer:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.85)',
                            lineHeight: 1.6
                          }}
                        >
                          {event.typicalAdminAnswers}
                        </Typography>
                      </Box>
                    ) : event.typicalCandidateQuestions && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontStyle: 'italic',
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        Sample question for your preparation...
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    Updated by: {event.createdBy}
                  </Typography>
                  {event.currentStep && (
                    <Chip
                      label={`Step ${parseStepNumber(event.currentStep)}/20`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {timelineEvents.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <TimelineIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            No updates available yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Your interview progress and updates will appear here as they become available.
          </Typography>
        </Box>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            font-size: 12px;
          }
          .MuiCard-root {
            box-shadow: none !important;
            border: 1px solid #ddd;
            break-inside: avoid;
          }
        }
      `}</style>

      {/* Question Dialog */}
      <Dialog
        open={questionDialog}
        onClose={() => setQuestionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <QuestionIcon color="info" />
            Ask a Question
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Submit your question about the interview process. The M4 Consulting team will respond and you'll see the answer in your timeline.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Your Question"
            placeholder="Type your question here..."
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            variant="outlined"
            disabled={submittingQuestion}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setQuestionDialog(false)}
            disabled={submittingQuestion}
          >
            Cancel
          </Button>
          <Button
            onClick={handleQuestionSubmit}
            variant="contained"
            color="info"
            disabled={!newQuestion.trim() || submittingQuestion}
          >
            {submittingQuestion ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Submit Question
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      <Footer />
    </Box>
  );
};

export default CandidateProfile;